<script setup lang="ts">
import { CheckIcon, ChevronDownIcon } from "lucide-vue-next"
import { computed, ref, watch } from "vue"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type ComboBoxOption = {
  value: string
  label: string
}

defineOptions({ inheritAttrs: false })
const modelValue = defineModel<string>({ default: "" })
const props = withDefaults(
  defineProps<{
    options?: ComboBoxOption[]
    placeholder?: string
    searchPlaceholder?: string
    emptyLabel?: string
    name?: string
  }>(),
  {
    placeholder: "Selecciona o escribe...",
    searchPlaceholder: "Buscar o escribir valor...",
    emptyLabel: "Pulsa Enter para usar el valor escrito",
  },
)

const open = ref(false)
const searchValue = ref("")

const triggerLabel = computed(() => {
  if (!modelValue.value) return props.placeholder
  return (
    props.options?.find((option) => option.value === modelValue.value)?.label ||
    modelValue.value
  )
})

watch(open, (isOpen) => {
  if (!isOpen) return
  searchValue.value = modelValue.value
})

function selectOption(optionValue: string) {
  modelValue.value = modelValue.value === optionValue ? "" : optionValue
  searchValue.value = modelValue.value
  open.value = false
}

function submitCustomValue() {
  const customValue = searchValue.value.trim()
  if (!customValue) return

  modelValue.value = customValue
  open.value = false
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        role="combobox"
        :aria-expanded="open"
        class="w-full justify-between"
        v-bind="$attrs"
      >
        {{ triggerLabel }}
        <ChevronDownIcon class="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-(--reka-popper-anchor-width) p-0">
      <Command>
        <CommandInput
          v-model="searchValue"
          :placeholder="searchPlaceholder"
          @keydown.enter.prevent="submitCustomValue"
        />
        <CommandList>
          <CommandEmpty>
            <Button
              v-if="searchValue.trim()"
              type="button"
              variant="ghost"
              class="h-auto w-full justify-start px-2 py-1.5 text-left"
              @click="submitCustomValue"
            >
              Usar "{{ searchValue.trim() }}"
            </Button>
            <span v-else>{{ emptyLabel }}</span>
          </CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="option in options"
              :key="option.value"
              :value="option.value"
              @select="() => selectOption(option.value)"
            >
              <CheckIcon
                :class="
                  cn(
                    'mr-2 h-4 w-4',
                    modelValue === option.value ? 'opacity-100' : 'opacity-0',
                  )
                "
              />
              {{ option.label }}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
  <input v-if="name" type="hidden" :name="name" :value="modelValue" />
</template>
